// © Thibault Sabathier, 2026. Tous droits réservés.
// frontend/src/utils/memberSearches.js
//
// Module « Recherches & entraide pro » : logique PURE côté interface (lot F1).
// Aucune dépendance React ni réseau : tout ici se teste sans DOM (le runner du dépôt tourne
// en `environment: 'node'`, cf. memberSearches.test.js).
//
// Source de vérité des règles métier : le backend (services/memberSearchRules.js). Ce fichier
// ne les REPRODUIT PAS : il prépare la saisie, traduit les codes d'erreur et construit les
// requêtes. Les plafonds (longueurs, nombre de recherches actives, durée) viennent de
// GET /api/member-searches/config, jamais de constantes recopiées ici.

/** Types de recherche (miroir de backend/config : slugs stables, jamais traduits). */
export const SEARCH_KINDS = ['opportunity_search', 'intro_request']

export const SEARCH_STATUSES = ['active', 'expired', 'closed']

/** Motifs de fermeture proposés au propriétaire (`moderation` est réservé aux admins : jamais saisi ici). */
export const OWNER_CLOSE_REASONS = ['found_via_terrilink', 'found_elsewhere', 'no_longer_needed']

/** Sous ce nombre de jours restants, l'échéance est mise en avant. */
export const EXPIRY_WARNING_DAYS = 7

// ─── Erreurs ────────────────────────────────────────────────────────────────

/** Code d'erreur API → clé du namespace `searches`. Tout code inconnu retombe sur `errors.generic`. */
const ERROR_KEYS = {
  VALIDATION: 'errors.validation',
  SEARCH_NOT_FOUND: 'errors.not_found',
  ACTIVE_LIMIT_REACHED: 'errors.active_limit',
  SCOPE_WIDEN_FORBIDDEN: 'errors.scope_widen_forbidden',
  SCOPE_NO_UNIT: 'errors.scope_no_unit',
  SEARCH_CLOSED: 'errors.closed',
  SEARCH_EXPIRED: 'errors.expired',
  SEARCH_NOT_EXPIRED: 'errors.not_expired',
  ALREADY_CLOSED: 'errors.already_closed',
  KIND_IMMUTABLE: 'errors.kind_immutable',
  NOT_OWNER: 'errors.not_owner',
  TOO_MANY_REQUESTS: 'errors.rate_limited',
  MEMBER_NOT_VALIDATED: 'errors.not_validated',
  MODULE_DISABLED: 'errors.module_disabled',
  MATCHING_CONSENT_REQUIRED: 'errors.consent_required',
  NOT_MATCHABLE: 'errors.not_matchable',
  ADMIN_ONLY: 'errors.admin_only',
  SETTINGS_UNAVAILABLE: 'errors.settings_unavailable',
}

export const errorKeyFor = (code) => ERROR_KEYS[code] || 'errors.generic'

/** Code de champ (réponse 422) → clé du namespace `searches`. */
const FIELD_ERROR_KEYS = {
  REQUIRED: 'field_errors.required',
  TOO_LONG: 'field_errors.too_long',
  TOO_MANY: 'field_errors.too_many',
  INVALID_VALUE: 'field_errors.invalid_value',
  INVALID_TYPE: 'field_errors.invalid_value',
  INVALID_CHARACTERS: 'field_errors.invalid_characters',
  HTML_NOT_ALLOWED: 'field_errors.html_not_allowed',
  HTTPS_REQUIRED: 'field_errors.https_required',
  INVALID_URL: 'field_errors.invalid_url',
  INVALID_DATE: 'field_errors.invalid_date',
  DATE_OUT_OF_RANGE: 'field_errors.date_out_of_range',
  INTRO_NEEDS_TARGET: 'field_errors.intro_needs_target',
  FIELD_NOT_ALLOWED: 'field_errors.invalid_value',
}

export const fieldErrorKeyFor = (code) => FIELD_ERROR_KEYS[code] || 'field_errors.invalid_value'

/**
 * Lit une erreur du client API : { code, status, fields }. `fields` = { champ: CODE } (réponse 422),
 * objet vide sinon. Ne lève jamais, quelle que soit la forme reçue (erreur réseau, page HTML nginx…).
 */
export const parseApiError = (error) => {
  const data = error && error.response && error.response.data
  const details = data && typeof data === 'object' ? data.details : null
  const fields =
    details && typeof details.fields === 'object' && details.fields ? details.fields : {}
  return {
    code: (error && error.code) || (data && data.error) || 'UNKNOWN',
    status: (error && error.status) || (error && error.response && error.response.status) || 0,
    fields,
  }
}

// ─── Formulaire ─────────────────────────────────────────────────────────────

/** Formulaire vide. Les champs de l'autre type ne sont jamais envoyés (le backend les refuserait). */
export const emptyForm = (kind) => ({
  kind,
  title: '',
  description: '',
  jobFamily: '',
  sector: '',
  country: '',
  city: '',
  remoteOk: false,
  contractTypes: [],
  availableFrom: '',
  cvUrl: '',
  targetCompanyName: '',
  scope: '',
})

/** Pré-remplit le formulaire d'édition à partir d'une recherche renvoyée par l'API. */
export const formFromSearch = (search) => ({
  ...emptyForm(search.kind),
  title: search.title || '',
  description: search.description || '',
  jobFamily: search.jobFamily || '',
  sector: search.sector || '',
  country: search.country || '',
  city: search.city || '',
  remoteOk: Boolean(search.remoteOk),
  contractTypes: Array.isArray(search.contractTypes) ? [...search.contractTypes] : [],
  availableFrom: search.availableFrom || '',
  cvUrl: search.cvUrl || '',
  targetCompanyName: search.targetCompanyName || '',
})

const trimmed = (v) => (typeof v === 'string' ? v.trim() : '')

/**
 * Corps de requête. Les champs vides sont ENVOYÉS vides (le backend les lit comme « non renseigné » :
 * c'est ce qui permet d'effacer une valeur à l'édition) ; seuls les champs propres au type sont inclus.
 *
 * @param {object} form
 * @param {{isEdit?: boolean}} [opts]  à l'édition, `kind` n'est pas envoyé (immuable)
 */
export const buildPayload = (form, { isEdit = false } = {}) => {
  const common = {
    title: trimmed(form.title),
    description: trimmed(form.description),
    jobFamily: trimmed(form.jobFamily),
    sector: trimmed(form.sector),
    country: trimmed(form.country),
    city: trimmed(form.city),
    remoteOk: Boolean(form.remoteOk),
  }
  const specific =
    form.kind === 'intro_request'
      ? { targetCompanyName: trimmed(form.targetCompanyName) }
      : {
          contractTypes: Array.isArray(form.contractTypes) ? [...new Set(form.contractTypes)] : [],
          availableFrom: trimmed(form.availableFrom),
          cvUrl: trimmed(form.cvUrl),
        }
  const payload = { ...common, ...specific }
  if (!isEdit) payload.kind = form.kind
  // Périmètre : uniquement 'network' ou 'unit' (jamais d'identifiant d'unité). Vide = défaut du tenant.
  if (form.scope === 'network' || form.scope === 'unit') payload.scope = form.scope
  return payload
}

// ─── Listes ─────────────────────────────────────────────────────────────────

/** Filtres de la liste du réseau → chaîne de requête. Valeurs vides ignorées, ordre stable. */
export const buildListQuery = (filters = {}, { page = 1, limit = 20 } = {}) => {
  const params = new URLSearchParams()
  const add = (key, value) => {
    if (value === undefined || value === null) return
    const v = typeof value === 'string' ? value.trim() : String(value)
    if (v !== '') params.set(key, v)
  }
  add('kind', filters.kind)
  add('jobFamily', filters.jobFamily)
  add('sector', filters.sector)
  add('country', filters.country)
  add('contractType', filters.contractType)
  if (filters.remoteOk === true) params.set('remoteOk', 'true')
  add('q', filters.q)
  if (page > 1) params.set('page', String(page))
  if (limit !== 20) params.set('limit', String(limit))
  return params.toString()
}

/** Nombre de filtres actifs (badge du bouton « Filtres »). `q` compte aussi. */
export const countActiveFilters = (filters = {}) =>
  ['kind', 'jobFamily', 'sector', 'country', 'contractType', 'q'].filter(
    (k) => typeof filters[k] === 'string' && filters[k].trim() !== '',
  ).length + (filters.remoteOk === true ? 1 : 0)

// ─── Affichage ──────────────────────────────────────────────────────────────

/** Couleur CoreUI du badge de statut. */
export const statusColor = (status) =>
  ({ active: 'success', expired: 'warning', closed: 'secondary' })[status] || 'secondary'

/** L'échéance mérite-t-elle d'être signalée ? (annonce active qui expire bientôt) */
export const isExpiringSoon = (search) =>
  Boolean(search) &&
  search.status === 'active' &&
  Number.isFinite(search.daysLeft) &&
  search.daysLeft <= EXPIRY_WARNING_DAYS

/**
 * Actions réellement proposées. L'API renvoie déjà `canEdit/canRenew/canReactivate/canClose`
 * (calculés côté serveur, qui reste l'autorité) : on ne fait que les lire.
 */
export const availableActions = (search) => {
  if (!search) return []
  const actions = []
  if (search.canEdit) actions.push('edit')
  if (search.canRenew) actions.push('renew')
  if (search.canReactivate) actions.push('reactivate')
  if (search.canClose) actions.push('close')
  return actions
}

/**
 * Lien de CV affichable : https uniquement, sans identifiants. Le serveur applique déjà cette règle
 * à l'écriture, mais l'interface ne rend JAMAIS un `href` qu'elle n'a pas vérifié elle-même
 * (une valeur posée autrement en base, `javascript:` par exemple, ne doit pas devenir un lien).
 * @returns {string|null} l'URL telle quelle, ou null
 */
export const safeHttpsUrl = (value) => {
  if (typeof value !== 'string') return null
  const raw = value.trim()
  if (!raw || /\s/.test(raw)) return null
  try {
    const url = new URL(raw)
    if (url.protocol !== 'https:' || !url.hostname || url.username || url.password) return null
    return raw
  } catch (e) {
    return null
  }
}

/**
 * Auteur anonymisé (RGPD) ou compte système : l'API le renvoie avec `id: null` et un libellé
 * « Membre supprimé » EN FRANÇAIS. L'interface n'affiche jamais ce libellé : elle affiche sa propre
 * traduction (`searches:author.deleted`) quand ce prédicat est vrai.
 */
export const isDeletedAuthor = (search) =>
  Boolean(search && search.author && search.author.id == null)

/** Nom affichable de l'auteur ; vide pour un auteur supprimé ou absent (l'appelant traduit). */
export const authorName = (search) => {
  const a = search && search.author
  if (!a || isDeletedAuthor(search)) return ''
  return [a.firstName, a.lastName].filter(Boolean).join(' ')
}

// ─── Offres correspondantes (lot 4) ─────────────────────────────────────────

/** Seule une recherche d'opportunité ACTIVE se rapproche d'offres (jamais une demande de mise en relation). */
export const canShowMatches = (search) =>
  Boolean(search && search.kind === 'opportunity_search' && search.status === 'active')

const MATCH_CRITERIA = ['contract', 'sector', 'location']

/**
 * Raison de correspondance (réponse de l'API) → { key, tone } pour l'affichage, ou null.
 * Seuls les critères qui CONCORDENT (« match ») ou que l'offre ne renseigne pas (« unknown ») sont
 * montrés : une incompatibilité écarte l'offre côté serveur, un critère non demandé n'a rien à dire.
 * Aucun score : chaque ligne dit POURQUOI l'offre est proposée, ou ce qui n'a pas pu être vérifié.
 */
export const reasonDescriptor = (reason) => {
  if (!reason || !MATCH_CRITERIA.includes(reason.criterion)) return null
  if (reason.status !== 'match' && reason.status !== 'unknown') return null
  const remote = reason.criterion === 'location' && reason.status === 'match' && reason.detail?.via === 'remote'
  return {
    key: `matching.reason.${reason.criterion}.${remote ? 'remote' : reason.status}`,
    tone: reason.status,
  }
}

/**
 * Drapeau de module (`modules.is_active`) : l'API le renvoie tel que MySQL le stocke (tinyint : 1 / 0), pas toujours
 * en booléen. Une comparaison stricte avec `true` masquait donc un module PARFAITEMENT actif (défaut constaté en
 * production le 14/10/2026). On accepte les formes réellement servies ; toute autre valeur reste refusée.
 */
export const isFlagOn = (value) => value === true || value === 1 || value === '1'

/**
 * L'onglet « Mes recherches » de la fiche membre : UNIQUEMENT sur SA PROPRE fiche, module Emploi actif.
 * Les recherches d'un autre membre ne s'y affichent jamais (elles suivent les règles de visibilité de la
 * page Emploi).
 */
export const canShowSearchesTab = ({ isOwnProfile, jobsEnabled }) => isOwnProfile === true && isFlagOn(jobsEnabled)

export const describeReasons = (reasons) =>
  (Array.isArray(reasons) ? reasons : []).map(reasonDescriptor).filter(Boolean)

// ─── Navigation vers « Mes recherches » ─────────────────────────────────────

/**
 * Onglet demandé par l'état de navigation d'une notification (`navigate('/jobs', { state: { tab: 'mine' } })`).
 * Renvoie 'mine' ou null (aucune demande : l'onglet courant ne doit PAS bouger). Ne lève jamais, quelle que soit
 * la forme de l'état (absent, null, autre type d'état posé par une autre page).
 */
export const jobsTabFromNavigationState = (state) =>
  state && typeof state === 'object' && state.tab === 'mine' ? 'mine' : null
